import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from '@/components/ui/use-toast'
import { Level, PharmacyStatus } from '@/gen/auth/common_pb'
import { GenerateAuthTokenRequest } from '@/gen/auth/generate_auth_token_pb'
import { GetPharmacyInfoRequest } from '@/gen/auth/get_pharmacy_info_pb'
import { GetUserAccessRequest, GetUserAccessResponse } from '@/gen/auth/get_user_access_pb'
import { generateAuthToken, getPharmacyInfo, getUserAccess } from '@/gen/pharmacy-PharmacyService_connectquery'
import { useMutation } from '@connectrpc/connect-query'
import { zodResolver } from "@hookform/resolvers/zod"
import { useRouter } from '@tanstack/react-router'
import { AnimatePresence, motion } from "framer-motion"
import { Boxes, Building2, ChevronRight, Eye, EyeOff, MapPin, Store } from 'lucide-react'
import { useState } from 'react'
import { useForm } from "react-hook-form"
import { z } from "zod"


// Validation schemas
const emailSchema = z.object({
  username: z.string().min(1, "Username is required").default(
    localStorage.getItem('rememberMeUsername') || ''
  ),
  rememberMe: z.boolean().default(false)
})

const passwordSchema = z.object({
  password: z.string().min(8, "Password must be at least 8 characters"),
  username: z.string(),
  rememberMe: z.boolean().default(false)
})

type EmailFormData = z.infer<typeof emailSchema>
type PasswordFormData = z.infer<typeof passwordSchema>

const Index = () => {
  const [step, setStep] = useState<'email' | 'password' | 'first-time' | 'access'>('email')
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()
  const [showPassword, setShowPassword] = useState(false)

  const firstTimePasswordSchema = z.object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    passwordRepeat: z.string().min(8, "Password must be at least 8 characters")
  }).refine((data) => data.password === data.passwordRepeat, {
    message: "Passwords do not match",
    path: ["passwordRepeat"],
  })

  type FirstTimeFormData = z.infer<typeof firstTimePasswordSchema>

  const firstTimeForm = useForm<FirstTimeFormData>({
    resolver: zodResolver(firstTimePasswordSchema),
    defaultValues: {
      password: '',
      passwordRepeat: ''
    }
  })

  // Placeholder for API call
  async function setFirstTimePasswordAPI({ username, password, passwordRepeat }: { username: string, password: string, passwordRepeat: string }) {
    // TODO: Replace with real API call
    return new Promise((resolve) => setTimeout(resolve, 1000))
  }

  const onFirstTimeSubmit = async (data: FirstTimeFormData) => {
    setIsLoading(true)
    try {
      await setFirstTimePasswordAPI({ username: email, ...data })
      toast({ title: "Password set successfully" })
      setStep('access')
    } catch (error: any) {
      firstTimeForm.setError('password', { type: 'manual', message: error?.message || 'Failed to set password' })
    } finally {
      setIsLoading(false)
    }
  }

  // Email form
  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      username: localStorage.getItem('rememberMeUsername') || '',
      rememberMe: false
    }
  })

  // Password form
  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
      username: email
    }
  })

  const userAccessMutation = useMutation(getUserAccess, {
    onSuccess: (response: GetUserAccessResponse) => {
      if (response.access.length === 0) {
        toast({
          title: 'No Access',
          description: 'You do not have access to any pharmacies. Please contact your administrator.',
          variant: 'destructive',
          duration: 5000
        })
        return
      }
      const accessList = response.access.map(item => ({
        id: item.id,
        name: item.name,
        level: item.level, // Store enum value directly
        roles: item.roles
      }))
      localStorage.setItem('userAccessList', JSON.stringify(accessList))

      // @ts-expect-error: isFirstTime may not be typed yet
      if (response.isFirstTime) {
        setStep('first-time')
        setIsLoading(false)
        return
      }
      setStep('access')
      setIsLoading(false)
    },
    onError: (error) => {
      console.error("Sign in error:", error)
      passwordForm.setError('password', {
        type: 'manual',
        message: 'Invalid credentials'
      })
    }
  })

  const accessTokenMutation = useMutation(generateAuthToken, {
    onSuccess: (response) => {
      // Store the token in localStorage
      localStorage.setItem('token', response.token)
      localStorage.setItem('refreshToken', response.refreshToken)
      localStorage.setItem('tokenExpiresAt', response.expiresAt.toString())
      localStorage.setItem('rememberMeUsername', passwordForm.getValues('username'))
      localStorage.setItem('roles', JSON.stringify(response.roles))
      localStorage.setItem('username', passwordForm.getValues('username'))
      localStorage.setItem('firstName', response.userFirstName || '')
      localStorage.setItem('lastName', response.userLastName || '')
      const pharmacyInfoRequest = new GetPharmacyInfoRequest()
      if (selectedId && selectedLevel) {
        if (selectedLevel === Level.COMPANY) {
          pharmacyInfoRequest.companyCode = selectedId
        }
        else if (selectedLevel === Level.BRANCH) {
          pharmacyInfoRequest.pharmacyCode = selectedId
        }
        else if (selectedLevel === Level.STOCK_POINT) {
          pharmacyInfoRequest.stockPointId = selectedId
        }
      }
      getPharmacyInfoMutation.mutate(pharmacyInfoRequest)
    },
    onError: (error) => {
      console.error("Access token error:", error)
      toast({
        title: 'Error',
        description: error?.message || 'Failed to generate access token',
        variant: 'destructive',
        duration: 5000
      })
    }
  })


  const getPharmacyInfoMutation = useMutation(getPharmacyInfo, {
    onSuccess: (response) => {
      if (response.status !== PharmacyStatus.STATUS_ACTIVE) {
        toast({
          title: 'Error',
          description: 'Pharmacy is not active',
          variant: 'destructive',
          duration: 5000
        })
        return
      }
      localStorage.setItem('level', selectedLevel?.toString() || ''),
      localStorage.setItem('pharmacyName', response.pharmacyName || '')
      localStorage.setItem('companyName', response.companyName || '')
      localStorage.setItem('companyCode', response.companyCode || '')
      localStorage.setItem('pharmacyCode', response.pharmacyCode || '')
      localStorage.setItem('stockPointId', response.stockPointId || '')
      localStorage.setItem('stockPointName', response.stockPointName || '')
      localStorage.setItem('pharmacyMetadata', JSON.stringify(response.metadata) || '')
      localStorage.setItem('stockPointType', response.stockPointType.toString() || '')
      localStorage.setItem('pharmacySettings', JSON.stringify(response.settings) || '')
      localStorage.setItem('pharmacyLocationDetails', JSON.stringify(response.locationDetails) || '')
      localStorage.setItem('pharmacySetupDetails', JSON.stringify(response.setupDetails) || '')
      localStorage.setItem('pharmacySize', response.size.toString() || '')
      
      
      // Use window.location.href instead of router.navigate
      if (selectedLevel === Level.COMPANY) {
        window.location.href = '/company/dashboard'
      } else if (selectedLevel === Level.BRANCH) {
        window.location.href = '/branch/dashboard'
      } else if (selectedLevel === Level.STOCK_POINT) {
        window.location.href = '/overview'
      } else {
        toast({
          title: 'Error',
          description: 'Invalid access level selected',
          variant: 'destructive',
          duration: 5000
        })
        return
      }
    },
    onError: (error) => {
      console.error("Pharmacy info error:", error)
      toast({
        title: 'Error',
        description: error?.message || 'Failed to fetch pharmacy info',
        variant: 'destructive',
        duration: 5000
      })
    }
  })

  

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null)

  const onEmailSubmit = async (data: EmailFormData) => {
    try {
      setIsLoading(true)
      setEmail(data.username)
      setStep('password')
      passwordForm.setValue('username', data.username)
      passwordForm.setValue('rememberMe', data.rememberMe)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to proceed to password step',
        variant: 'destructive',
        duration: 5000
      })
      console.error("Email submit error:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const onPasswordSubmit = async () => {
    try {
      setIsLoading(true)
      const data = new GetUserAccessRequest()
      data.username = passwordForm.getValues('username')
      data.password = passwordForm.getValues('password')
      userAccessMutation.mutate(data)
    } catch (error: any) {
      console.error("Password submit error:", error)
      // Set form error
      passwordForm.setError('password', {
        type: 'manual',
        message: error?.response?.data?.message || error?.message || 'Invalid credentials'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-auto bg-gradient-overlay vector-overlay">
      <div className="relative z-10 flex flex-col items-center gap-8">
        <AnimatePresence mode="wait">
          {step === 'email' ? (
            <motion.div
              key="email-logo"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <img src="/logo.svg" alt="Logo" className="w-12 h-auto filter" />
            </motion.div>
          ) : step === 'password' ? (
            <motion.div
              key="password-logos"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-4"
            >
              <img src="/logo.svg" alt="Logo" className="w-12 h-auto" />
              <div className="flex items-center gap-[-8px]">
                <ChevronRight className="w-6 h-6 text-white" />
                <ChevronRight className="w-6 h-6 text-white -ml-4" />
                <ChevronRight className="w-6 h-6 text-white -ml-4" />
              </div>
              <div className="w-12 h-12 rounded-full bg-[#2C2C35] flex items-center justify-center">
                <Store className="w-6 h-6 text-white" />
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {step === 'email' ? (
            <motion.div
              key="email"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="relative z-10"
            >
              <Card className="w-[400px] h-[320px] bg-[#1C1C24] border-0">
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold text-white text-center">Sign in to your account</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Form {...emailForm}>
                    <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-6">
                      <FormField
                        control={emailForm.control}
                        name="username"
                        render={({ field }) => (
                          <FormItem>
                            <Label className="text-sm text-gray-300">Username</Label>
                            <FormControl>
                              <Input 
                                {...field}
                                className="bg-[#2C2C35] border-gray-700 text-white focus:border-[#474DFF] focus:ring-[#474DFF]"
                                placeholder="Enter your username"
                              />
                            </FormControl>
                            <FormMessage className="text-red-500" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={emailForm.control}
                        name="rememberMe"
                        render={({ field }) => (
                          <FormItem className="flex gap-2 items-center">
                            <FormControl>
                              <input 
                                type="checkbox" 
                                checked={field.value}
                                onChange={field.onChange}
                                className="rounded bg-[#2C2C35] border-gray-700 text-[#474DFF] focus:ring-[#474DFF] mt-1"
                              />
                            </FormControl>
                            <Label className="text-sm text-gray-300">
                              Remember me
                            </Label>
                          </FormItem>
                        )}
                      />  

                      <Button 
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#09090B] text-white hover:bg-[#09090B]/90"
                      >
                        {isLoading ? 'Loading...' : 'Continue'}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </motion.div>
          ) : step === 'password' ? (
            <motion.div
              key="password"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="relative z-10"
            >
              <div className="flex items-center justify-between mb-4 w-[400px]">
                <button 
                  onClick={() => setStep('email')}
                  className="text-sm text-gray-300 hover:text-white transition-colors flex items-center gap-1.5"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-left"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
                  Back
                </button>
              </div>
              <Card className="w-[400px] h-[320px] bg-[#1C1C24] border-0">
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold text-white text-center">Enter your password</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Form {...passwordForm}>
                    <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between bg-[#2C2C35] p-3 rounded-md">
                          <span className="text-sm text-gray-300">{email}</span>
                        </div>
                        <FormField
                          control={passwordForm.control as any}
                          name="password"
                          render={({ field }) => (
                            <FormItem>
                              <Label className="text-sm text-gray-300">Password</Label>
                              <FormControl>
                                <div className="relative">
                                  <Input 
                                    {...field}
                                    type={showPassword ? "text" : "password"}
                                    className="bg-[#2C2C35] border-gray-700 text-white focus:border-[#474DFF] focus:ring-[#474DFF] pr-10"
                                    placeholder="Enter your password"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                  >
                                    {showPassword ? (
                                      <EyeOff className="w-4 h-4" />
                                    ) : (
                                      <Eye className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-500" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <Button 
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#09090B] text-white hover:bg-[#09090B]/90"
                      >
                        {isLoading ? 'Loading...' : 'Sign in'}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
              <div className="flex justify-center w-[400px] mt-0">
                <Button
                  type="button"
                  variant="link"
                  className="text-gray-300 underline hover:text-white px-0"
                  onClick={() => router.navigate({ to: "/forgot-password" })}
                >
                  Did you forget your password?
                </Button>
              </div>
            </motion.div>
          ) : step === 'first-time' ? (
            <motion.div
              key="first-time"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="relative z-10"
            >
              <Card className="w-[400px] h-[320px] bg-[#1C1C24] border-0">
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold text-white text-center">Set New Password</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Form {...firstTimeForm}>
                    <form onSubmit={firstTimeForm.handleSubmit(onFirstTimeSubmit)} className="space-y-6">
                      <div className="space-y-4">
                        <FormField
                          control={firstTimeForm.control}
                          name="password"
                          render={({ field }) => (
                            <FormItem>
                              <Label className="text-sm text-gray-300">Enter New Password</Label>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    {...field}
                                    type={showPassword ? "text" : "password"}
                                    className="bg-[#2C2C35] border-gray-700 text-white focus:border-[#474DFF] focus:ring-[#474DFF] pr-10"
                                    placeholder="Enter your password"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                  >
                                    {showPassword ? (
                                      <EyeOff className="w-4 h-4" />
                                    ) : (
                                      <Eye className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-500" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={firstTimeForm.control}
                          name="passwordRepeat"
                          render={({ field }) => (
                            <FormItem>
                              <Label className="text-sm text-gray-300">Confirm New Password</Label>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    {...field}
                                    type={showPassword ? "text" : "password"}
                                    className="bg-[#2C2C35] border-gray-700 text-white focus:border-[#474DFF] focus:ring-[#474DFF] pr-10"
                                    placeholder="Confirm your password"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                  >
                                    {showPassword ? (
                                      <EyeOff className="w-4 h-4" />
                                    ) : (
                                      <Eye className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-500" />
                            </FormItem>
                          )}
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#09090B] text-white hover:bg-[#09090B]/90"
                      >
                        {isLoading ? 'Loading...' : 'Set Password'}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key="access"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="relative z-10 w-screen h-screen px-8 py-4 bg-white"
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex flex-col items-center justify-center mb-8">
                  <div className="flex flex-row items-center gap-4">
                    <div className="w-12 h-12 rounded-md bg-blue-600 flex items-center justify-center">
                      <img src="/logo.svg" alt="Logo" className="w-8 h-8" />
                    </div>
                    <h1 className="text-2xl font-semibold text-black">Select Pharmacy Access</h1>
                  </div>
                  <p className="text-gray-500 text-center text-base max-w-3xl mt-2">Configure your pharmacy access by selecting your company, branch and stock points</p>
                </div>

                {/* Vertical Layout */}
                <div className="flex flex-col gap-6 flex-1 overflow-auto pb-4">
                  {/* Company Section */}
                  <div className="flex flex-col bg-white shadow-xl rounded-lg p-6 min-h-[200px] max-h-[280px]">
                    <div className="flex flex-row items-center gap-2 mb-1 px-2">
                      <Building2 className="w-6 h-6 text-black" />
                      <h2 className="text-xl font-semibold text-black">Company</h2>
                    </div>
                    <p className="text-gray-500 text-sm mb-4 px-2">Select your organisation</p>
                    <div className="overflow-y-auto min-h-0 flex-1 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                      <div className="grid grid-cols-4 gap-4 content-start">
                        {userAccessMutation.data?.access
                          .filter(access => access.level === Level.COMPANY)
                          .map((access) => (
                            <div key={access.id} 
                            className="flex items-center justify-between bg-white p-3 rounded-md border-[0.5px] cursor-pointer border-gray-300 hover:border-blue-600 transition-colors"
                            onClick={() => {
                              const data = new GenerateAuthTokenRequest()
                              data.username = passwordForm.getValues('username')
                              data.password = passwordForm.getValues('password')
                              data.id = access.id
                              data.level = access.level
                              accessTokenMutation.mutate(data)
                              setIsLoading(true)
                              setSelectedId(access.id)
                              setSelectedLevel(access.level)
                            }}
                            >
                              <span className="text-sm text-black font-medium truncate mr-3">{access.name}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={() => (e: { stopPropagation: () => any }) => e.stopPropagation()}

                              >
                                Select
                              </Button>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>

                  {/* Branch Section */}
                  <div className="flex flex-col bg-white shadow-xl rounded-lg p-6 min-h-[200px] max-h-[280px]">
                    <div className="flex flex-row items-center gap-2 mb-1 px-2">
                      <MapPin className="w-6 h-6 text-black" />
                      <h2 className="text-xl font-semibold text-black">Branch</h2>
                    </div>
                    <p className="text-gray-500 text-sm mb-4 px-2">Choose your pharmacy branch</p>
                    <div className="overflow-y-auto min-h-0 flex-1 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                      <div className="grid grid-cols-4 gap-4 content-start">
                        {userAccessMutation.data?.access
                          .filter(access => access.level === Level.BRANCH)
                          .map((access) => (
                            <div key={access.id} 
                            className="flex items-center justify-between bg-white p-3 rounded-md border-[0.5px] cursor-pointer border-gray-300 hover:border-blue-600 transition-colors"
                            onClick={() => {
                              const data = new GenerateAuthTokenRequest()
                              data.username = passwordForm.getValues('username')
                              data.password = passwordForm.getValues('password')
                              data.id = access.id
                              data.level = access.level
                              accessTokenMutation.mutate(data)
                              setIsLoading(true)
                              setSelectedId(access.id)
                              setSelectedLevel(access.level)
                            }}
                            >
                              <span className="text-sm text-black font-medium truncate mr-3">{access.name}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={() => (e: { stopPropagation: () => any }) => e.stopPropagation()}
                              >
                                Select
                              </Button>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>

                  {/* Stock Point Section */}
                  <div className="flex flex-col bg-white shadow-xl rounded-lg p-6 min-h-[200px] max-h-[280px]">
                    <div className="flex flex-row items-center gap-2 mb-1 px-2">
                      <Boxes className="w-6 h-6 text-black" />
                      <h2 className="text-xl font-semibold text-black">Stock Point</h2>
                    </div>
                    <p className="text-gray-500 text-sm mb-4 px-2">Select multiple stock points for inventory management</p>
                    <div className="overflow-y-auto min-h-0 flex-1 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                      <div className="grid grid-cols-4 gap-4 content-start">
                        {userAccessMutation.data?.access
                          .filter(access => access.level === Level.STOCK_POINT)
                          .map((access) => (
                            <div key={access.id} 
                            className="flex items-center justify-between bg-white p-3 rounded-md border-[0.5px] cursor-pointer border-gray-300 hover:border-blue-600 transition-colors"
                            onClick={() => {
                              const data = new GenerateAuthTokenRequest()
                              data.username = passwordForm.getValues('username')
                              data.password = passwordForm.getValues('password')
                              data.id = access.id
                              data.level = access.level
                              accessTokenMutation.mutate(data)
                              setIsLoading(true)
                              setSelectedId(access.id)
                              setSelectedLevel(access.level)
                            }}
                            >
                              <span className="text-sm text-black font-medium truncate mr-3">{access.name}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={() => (e: { stopPropagation: () => any }) => e.stopPropagation()}
                              >
                                Select
                              </Button>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default Index